import { ArrowDown, ArrowUp, Code2, Copy, Download, Image, X } from "lucide-react";
import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../../ui/dialog";
import {
	COMPARISON_OPTIONS,
	LakeQueryResponse,
	WidgetDisplay,
	WidgetSpec,
	widgetTitle,
} from "../../../models/ReportBuilder";
import WidgetChart, { ChartPoint, resolveKeys, deltaPercent } from "./WidgetChart";
import { formatBucketLabel, grainOfLabel } from "./dateRange";
import {
	arrangeRows,
	copyChartImage,
	downloadText,
	drillPath,
	formatNumber,
	formatPercent,
	isNumericColumn,
	toCsv,
	toTsv,
	visibleColumns,
} from "./format";

interface WidgetMaximizeDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	widget: { spec: WidgetSpec | Omit<WidgetSpec, "sql">; display?: WidgetDisplay };
	data?: LakeQueryResponse;
	/** The period the rows cover, for the header. */
	periodLabel: string;
	/** Hidden on public pages. */
	showSql?: boolean;
	/** Row the viewer clicked in the card; the table opens filtered to it. */
	initialPoint?: ChartPoint;
}

type SortState = { key: string; direction: "asc" | "desc" } | undefined;

/**
 * Full-width view of one widget: the chart on top, every row below with
 * sortable columns and the comparison, plus export and copy actions.
 * Clicking a bar or point filters the table to that x value.
 */
const WidgetMaximizeDialog: React.FC<WidgetMaximizeDialogProps> = ({
	open,
	onOpenChange,
	widget,
	data,
	periodLabel,
	showSql = true,
	initialPoint,
}) => {
	const [sort, setSort] = useState<SortState>();
	const [point, setPoint] = useState<ChartPoint | undefined>(initialPoint);
	const [sqlOpen, setSqlOpen] = useState(false);
	const [notice, setNotice] = useState<string>();
	const chartRef = useRef<HTMLDivElement>(null);
	const { title, description } = widgetTitle(widget);
	const display = widget.display;
	const unit = display?.unit?.trim() || widget.spec.chart.unit;

	React.useEffect(() => {
		if (open) {
			setPoint(initialPoint);
			setSort(undefined);
			setNotice(undefined);
		}
	}, [open, initialPoint]);

	const keys = useMemo(
		() => (data ? resolveKeys(widget.spec, data) : { xKey: "", yKeys: [] as string[] }),
		[widget.spec, data]
	);
	const arranged = useMemo(
		() => (data ? arrangeRows(data, display, keys.xKey) : undefined),
		[data, display, keys.xKey]
	);
	const columns = arranged ? visibleColumns(arranged.columns, display) : [];
	const numericColumns = arranged ? columns.filter((c) => isNumericColumn(arranged.rows, c)) : [];
	const cmpLabel =
		COMPARISON_OPTIONS.find((o) => o.value === data?.comparison?.mode)?.short ?? "jämförelse";

	const rows = useMemo(() => {
		if (!arranged) return [];
		let indexed = arranged.rows.map((row, i) => ({ row, other: arranged.comparison?.rows[i] }));
		if (point) indexed = indexed.filter(({ row }) => row[keys.xKey] === point.value);
		if (sort) {
			const numeric = numericColumns.includes(sort.key);
			indexed = [...indexed].sort((a, b) => {
				const va = a.row[sort.key];
				const vb = b.row[sort.key];
				const cmp = numeric
					? Number(va ?? 0) - Number(vb ?? 0)
					: String(va ?? "").localeCompare(String(vb ?? ""), "sv");
				return sort.direction === "desc" ? -cmp : cmp;
			});
		}
		return indexed;
	}, [arranged, point, sort, keys.xKey, numericColumns]);

	const toggleSort = (key: string) =>
		setSort((s) =>
			s?.key === key
				? s.direction === "desc"
					? { key, direction: "asc" }
					: undefined
				: { key, direction: "desc" }
		);

	const exportRows = rows.map(({ row }) => row);
	const fileBase =
		title
			.replace(/[^\p{L}\p{N}]+/gu, "-")
			.replace(/^-|-$/g, "")
			.toLowerCase() || "widget";
	const flash = (text: string) => {
		setNotice(text);
		window.setTimeout(() => setNotice(undefined), 2500);
	};

	const totals = useMemo(() => {
		const out: Record<string, number> = {};
		numericColumns.forEach(
			(c) => (out[c] = rows.reduce((sum, { row }) => sum + Number(row[c] ?? 0), 0))
		);
		return out;
	}, [rows, numericColumns]);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				hideCloseButton
				className="z-[1300] flex h-[94vh] w-[calc(100vw-2rem)] max-w-none flex-col gap-0 overflow-hidden p-0"
			>
				<DialogHeader className="border-b px-5 py-3">
					<div className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<DialogTitle className="truncate">{title}</DialogTitle>
							<DialogDescription className="truncate">
								{description ? `${description} · ` : ""}
								{periodLabel}
								{data?.dataAsOf
									? ` · data från ${new Date(data.dataAsOf).toLocaleString("sv-SE")}`
									: ""}
							</DialogDescription>
						</div>
						<div className="flex shrink-0 items-center gap-1">
							{notice && <span className="mr-2 text-xs text-brand">{notice}</span>}
							<button
								type="button"
								onClick={() => {
									downloadText(`${fileBase}.csv`, toCsv(columns, exportRows));
								}}
								className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
								title="Ladda ner som CSV (Excel)"
							>
								<Download className="h-3.5 w-3.5" /> CSV
							</button>
							<button
								type="button"
								onClick={() =>
									navigator.clipboard
										.writeText(toTsv(columns, exportRows))
										.then(() => flash("Tabellen kopierad"))
								}
								className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
								title="Kopiera tabellen (klistra in i Excel)"
							>
								<Copy className="h-3.5 w-3.5" /> Kopiera data
							</button>
							{widget.spec.chart.type !== "table" && widget.spec.chart.type !== "number" && (
								<button
									type="button"
									onClick={() =>
										chartRef.current &&
										copyChartImage(chartRef.current)
											.then(() => flash("Bilden kopierad"))
											.catch((e: Error) => flash(e.message))
									}
									className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
									title="Kopiera diagrammet som bild"
								>
									<Image className="h-3.5 w-3.5" /> Kopiera bild
								</button>
							)}
							{showSql && "sql" in widget.spec && (
								<button
									type="button"
									onClick={() => setSqlOpen((s) => !s)}
									className={cn(
										"inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium hover:bg-gray-50",
										sqlOpen ? "bg-gray-900 text-white hover:bg-gray-800" : "text-gray-700"
									)}
								>
									<Code2 className="h-3.5 w-3.5" /> SQL
								</button>
							)}
							<button
								type="button"
								onClick={() => onOpenChange(false)}
								className="ml-1 rounded p-1 text-gray-500 hover:bg-gray-100"
								aria-label="Stäng"
							>
								<X className="h-4 w-4" />
							</button>
						</div>
					</div>
				</DialogHeader>

				<div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
					{sqlOpen && "sql" in widget.spec && (
						<pre className="m-5 mb-0 max-h-48 shrink-0 overflow-auto whitespace-pre-wrap break-words rounded bg-gray-900 p-3 text-[11px] leading-snug text-gray-100">
							{widget.spec.sql}
						</pre>
					)}
					{data && widget.spec.chart.type !== "table" && (
						<div
							ref={chartRef}
							className="shrink-0 px-5 pt-4"
							style={{ height: widget.spec.chart.type === "number" ? 180 : "42vh" }}
						>
							<WidgetChart
								spec={widget.spec}
								data={data}
								display={display}
								height="100%"
								onPointClick={(p) => setPoint((cur) => (cur?.value === p.value ? undefined : p))}
							/>
						</div>
					)}

					<div className="px-5 pb-5 pt-3">
						<div className="mb-2 flex items-center justify-between text-xs text-gray-500">
							<span>
								{rows.length} rader
								{data?.truncated ? " (avkortat av radtaket)" : ""}
								{point ? (
									<>
										{" "}
										· filtrerat på{" "}
										<span className="font-medium text-gray-800">
											{grainOfLabel(point.value)
												? formatBucketLabel(point.value, true)
												: String(point.value)}
										</span>{" "}
										<button
											type="button"
											onClick={() => setPoint(undefined)}
											className="text-action hover:underline"
										>
											visa alla
										</button>
									</>
								) : widget.spec.chart.type !== "table" && widget.spec.chart.type !== "number" ? (
									" · klicka i diagrammet för att filtrera"
								) : null}
							</span>
							{data?.comparison && (
								<span>
									jämfört med {data.comparison.range.from} – {data.comparison.range.to}
								</span>
							)}
						</div>
						{!data && <p className="text-sm text-gray-500">Ingen data.</p>}
						{data && (
							<div className="overflow-x-auto rounded-md border border-gray-200">
								<table className="w-full text-sm">
									<thead className="sticky top-0 bg-gray-50">
										<tr>
											{columns.map((c) => (
												<React.Fragment key={c}>
													<th
														onClick={() => toggleSort(c)}
														className={cn(
															"cursor-pointer select-none whitespace-nowrap border-b px-2 py-1.5 font-medium text-gray-700 hover:bg-gray-100",
															numericColumns.includes(c) ? "text-right" : "text-left"
														)}
													>
														<span className="inline-flex items-center gap-1">
															{c}
															{sort?.key === c &&
																(sort.direction === "desc" ? (
																	<ArrowDown className="h-3 w-3" />
																) : (
																	<ArrowUp className="h-3 w-3" />
																))}
														</span>
													</th>
													{data.comparison && numericColumns.includes(c) && (
														<>
															<th className="whitespace-nowrap border-b px-2 py-1.5 text-right font-normal text-gray-400">
																{cmpLabel}
															</th>
															<th className="border-b px-2 py-1.5 text-right font-normal text-gray-400">
																Δ %
															</th>
														</>
													)}
												</React.Fragment>
											))}
										</tr>
									</thead>
									<tbody>
										{rows.map(({ row, other }, i) => (
											<tr key={i} className="odd:bg-white even:bg-gray-50/60">
												{columns.map((c) => {
													const value = row[c];
													const numeric = numericColumns.includes(c);
													const href = !numeric ? drillPath(row, c, data.columns) : undefined;
													const text = numeric
														? formatNumber(value, undefined, display?.format)
														: grainOfLabel(value)
														? formatBucketLabel(value, true)
														: String(value ?? "");
													const pct =
														numeric && data.comparison ? deltaPercent(value, other?.[c]) : null;
													return (
														<React.Fragment key={c}>
															<td
																className={cn(
																	"whitespace-nowrap px-2 py-1",
																	numeric && "text-right tabular-nums"
																)}
															>
																{href ? (
																	<Link
																		to={href}
																		className="text-action underline-offset-2 hover:underline"
																	>
																		{text}
																	</Link>
																) : (
																	text
																)}
															</td>
															{numeric && data.comparison && (
																<td className="px-2 py-1 text-right tabular-nums text-gray-400">
																	{formatNumber(other?.[c], undefined, display?.format)}
																</td>
															)}
															{numeric && data.comparison && (
																<td
																	className={cn(
																		"px-2 py-1 text-right tabular-nums text-xs",
																		pct === null
																			? "text-gray-400"
																			: pct >= 0
																			? "text-brand"
																			: "text-danger"
																	)}
																>
																	{pct === null ? "–" : formatPercent(pct)}
																</td>
															)}
														</React.Fragment>
													);
												})}
											</tr>
										))}
									</tbody>
									{rows.length > 1 && numericColumns.length > 0 && (
										<tfoot className="bg-gray-50">
											<tr>
												{columns.map((c, i) => (
													<React.Fragment key={c}>
														<td
															className={cn(
																"border-t px-2 py-1 font-medium",
																numericColumns.includes(c) ? "text-right tabular-nums" : "text-left"
															)}
														>
															{numericColumns.includes(c)
																? formatNumber(totals[c], unit, display?.format)
																: i === 0
																? "Summa"
																: ""}
														</td>
														{numericColumns.includes(c) && data.comparison && (
															<td className="border-t" colSpan={2} />
														)}
													</React.Fragment>
												))}
											</tr>
										</tfoot>
									)}
								</table>
							</div>
						)}
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default WidgetMaximizeDialog;
